import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Tag } from '@neuroedge/shared';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import { useCreateTag, useDeleteTag, useTags, useUpdateTag } from '../../api/tags';
import { TagChip } from './TagChips';
import { validateTag } from './tag-validation';

function TagRow({ tag, canManage }: { tag: Tag; canManage: boolean }) {
  const update = useUpdateTag();
  const del = useDeleteTag();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color ?? '#3366ff');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function save() {
    const errors = validateTag(name, color);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    try {
      await update.mutateAsync({ id: tag.id, patch: { name: name.trim(), color } });
      setEditing(false);
      toast.success('Tag updated.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
          return;
        }
      }
      const message = err instanceof Error ? err.message : 'Could not save.';
      setError(message);
      toast.error(message);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete "${tag.name}"? It will be removed from every contact and lead.`)) {
      return;
    }
    try {
      await del.mutateAsync(tag.id);
      toast.success('Tag deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the tag.');
    }
  }

  return (
    <tr>
      <td>{editing ? null : <TagChip tag={tag} />}</td>
      <td>
        {editing ? (
          <>
            <input
              className={['ne-input', fieldErrors.name ? 'ne-input--error' : '']
                .filter(Boolean)
                .join(' ')}
              value={name}
              maxLength={60}
              aria-invalid={fieldErrors.name ? true : undefined}
              onChange={(e) => {
                setName(e.target.value);
                setFieldErrors((f) => {
                  if (!f.name) return f;
                  const { name: _removed, ...rest } = f;
                  return rest;
                });
              }}
            />
            {fieldErrors.name ? (
              <span className="ne-field__error" role="alert">
                {fieldErrors.name}
              </span>
            ) : null}
          </>
        ) : (
          tag.name
        )}
      </td>
      <td>
        {editing ? (
          <>
            <input
              type="color"
              className="ne-input"
              style={{ width: 48, padding: 2 }}
              value={color}
              onChange={(e) => {
                setColor(e.target.value);
                setFieldErrors((f) => {
                  if (!f.color) return f;
                  const { color: _removed, ...rest } = f;
                  return rest;
                });
              }}
            />
            {fieldErrors.color ? (
              <span className="ne-field__error" role="alert">
                {fieldErrors.color}
              </span>
            ) : null}
          </>
        ) : (
          (tag.color ?? '—')
        )}
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {!canManage ? null : editing ? (
          <>
            <Button disabled={update.isPending} onClick={() => void save()}>
              Save
            </Button>{' '}
            <Button
              variant="secondary"
              onClick={() => {
                setName(tag.name);
                setColor(tag.color ?? '#3366ff');
                setError(null);
                setFieldErrors({});
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Edit
            </Button>{' '}
            <Button variant="secondary" disabled={del.isPending} onClick={() => void remove()}>
              Delete
            </Button>
          </>
        )}
        {error ? (
          <div style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {error}
          </div>
        ) : null}
      </td>
    </tr>
  );
}

export function TagsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('tags.manage');
  const tags = useTags();
  const create = useCreateTag();
  const toast = useToast();

  const [name, setName] = useState('');
  const [color, setColor] = useState('#3366ff');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const errors = validateTag(name, color);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    try {
      await create.mutateAsync({ name: name.trim(), color });
      setName('');
      setFieldErrors({});
      toast.success('Tag created.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
          return;
        }
      }
      const message = err instanceof Error ? err.message : 'Could not create the tag.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 720 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Tags</h1>

      {canManage ? (
        <Card title="New tag">
          <form
            onSubmit={onCreate}
            style={{ display: 'flex', alignItems: 'end', gap: 'var(--space-2)', flexWrap: 'wrap' }}
          >
            <TextField
              label="Name"
              placeholder="e.g. VIP"
              value={name}
              maxLength={60}
              onChange={(e) => {
                setName(e.target.value);
                setFieldErrors((f) => {
                  if (!f.name) return f;
                  const { name: _removed, ...rest } = f;
                  return rest;
                });
              }}
              error={fieldErrors.name}
            />
            <div className="ne-field">
              <label className="ne-field__label" htmlFor="tag-color">
                Colour
              </label>
              <input
                id="tag-color"
                type="color"
                className="ne-input"
                style={{ width: 48, padding: 2 }}
                value={color}
                onChange={(e) => {
                  setColor(e.target.value);
                  setFieldErrors((f) => {
                    if (!f.color) return f;
                    const { color: _removed, ...rest } = f;
                    return rest;
                  });
                }}
              />
              {fieldErrors.color ? (
                <span className="ne-field__error" role="alert">
                  {fieldErrors.color}
                </span>
              ) : null}
            </div>
            <Button type="submit" disabled={create.isPending || name.trim() === ''}>
              Create
            </Button>
          </form>
          {error ? (
            <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-danger)' }}>{error}</p>
          ) : null}
        </Card>
      ) : null}

      <Card>
        {tags.isPending ? (
          <PageState kind="loading" message="Loading tags…" />
        ) : tags.isError ? (
          <PageState
            kind="error"
            message={tags.error.message}
            onRetry={() => void tags.refetch()}
          />
        ) : tags.data.length === 0 ? (
          <PageState kind="empty" title="No tags" message="Create a tag to start segmenting." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ne-table">
              <thead>
                <tr>
                  <th>Preview</th>
                  <th>Name</th>
                  <th>Colour</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tags.data.map((t) => (
                  <TagRow key={t.id} tag={t} canManage={canManage} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
